import ProductDetailsFields, { ItemDetailsTabs, revealItemInvalid } from "./ProductDetailsFields";
import { catalogReferenceClient, pendingReferenceRow } from "../catalogApprovals/catalogReferenceOptions";
import useCatalogAccess from "../useCatalogAccess";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import InternalCodeSuggestions from "../InternalCodeSuggestions";
import { catalogRequestError, duplicateInternalCodeMessage, hasInternalCodeConflict } from "../internalCatalogCodes";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import ItemClassificationPicker from "./ItemClassificationPicker";
import ItemConsumptionFrequencies, { consumptionFrequencyDraft, consumptionUnits, serializeConsumptionFrequencies } from "./ItemConsumptionFrequencies";
import AddSupplierPopup from "../AddSupplierPopup";
import ItemFormNotice from "./ItemFormNotice";
import ItemUnitCompatibility, { useItemUnitCompatibility } from "./ItemUnitCompatibility";
import ItemSubunitPreview from "./ItemSubunitPreview";
import { useOriginalDocumentDialogScope } from "../originals/OriginalDocumentDialogScope";
import { supplierPermissions } from "../supplierMessages";
import { readStoredUser } from "../../../utils/sessionUser";
import axios from "../../../api/axiosInstance";
import useIsMobile from "../../../hooks/useIsMobile";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { getUnitName, unitSearchText } from "../../utils/unitNames";
import { inlineItemText } from "./inlineItemMessages";
import { purchaseConstraintFields, serializePurchaseConstraints } from "./itemPurchaseConstraints";
import ItemParUnitSelect from "./ItemParUnitSelect";
import { parStockUnits, serializeParStock } from "./itemParStockUnits";
import "../../PurchasingUi.css";
import "./PurchasingInlineItem.css";
import "./ItemPurchaseFields.css";
import {
  inventoryPolicySelectOptionsForItem,
  isDirectConsumptionItem,
} from "../itemsFamiliesUi";

const blankForm = (lockedSupplierId = null) => ({
  code: "",
  supplier_code: "",
  name: "",
  description: "",
  product_details: null,
  family_id: "",
  subfamily_id: "",
  category_id: "",
  subcategory_id: "",
  inventory_policy: "",
  unit_id: "",
  subunit_id: "",
  pack_size: "",
  minimum_purchase_quantity: "",
  purchase_multiple: "",
  subunit2_id: "",
  subunit2_size: "",
  suppliers: lockedSupplierId ? [String(lockedSupplierId)] : [],
});

const makeOption = (value, label, meta = "") => ({
  value: value === null || value === undefined ? "" : String(value),
  label,
  meta,
  searchText: `${label} ${meta}`.toLowerCase(),
});

const getSelectedOption = (options, value, fallbackLabel) =>
  options.find((option) => String(option.value) === String(value)) ||
  (fallbackLabel ? makeOption(value, fallbackLabel) : null);

export default function AddItemPopup({
  isOpen,
  onClose,
  onAdded,
  supplierIdLock = null,
  context = null,
  editorAdapter = null,
  initialItem = null,
  existingRecords = [],
  requestClient = axios,
  approvalRequest = null,
}) {
  const isMobile = useIsMobile();
  const { themeId, languageId: userLanguageId } = usePmsTheme();
  const { isSplit } = useOriginalDocumentDialogScope();
  const languageId = editorAdapter?.languageId || userLanguageId;
  const t = useCallback((text) => inlineItemText(languageId, text), [languageId]);
  const dialogId = useId();
  const modalRef = useRef(null);
  const pickerRef = useRef(null);
  const pickerTriggerRef = useRef(null);
  const supplierCreatorRef = useRef(null);
  const supplierReturnFocusRef = useRef(null);
  const dismissRef = useRef(null);
  const lockedSupplierId = supplierIdLock ? Number(supplierIdLock) : null;
  const isRequisitionContext = context === "requisition";
  const inventoryPolicyEnabled = editorAdapter?.inventoryPolicyEnabled !== false;
  const currentUser = readStoredUser();
  const businessUnitId = useRef(currentUser?.active_business_unit_id).current;
  const canCreateSupplier = !editorAdapter && !lockedSupplierId && Boolean(businessUnitId) && supplierPermissions(currentUser).create;
  const access = useCatalogAccess(businessUnitId);
  const permission = "purchasing.items.create";
  const canCreateItem = Boolean(editorAdapter) || access.can(permission);
  const canSuggestClassification = !editorAdapter && canCreateItem;
  const requestOptions = useCallback(() => {
    const options = access.requestConfig(permission);
    delete options.params;
    return options;
  }, [access]);

  const [form, setForm] = useState(blankForm(lockedSupplierId));
  const [detailsTab, setDetailsTab] = useState("general");
  const [detailsBusy, setDetailsBusy] = useState(false);
  const detailsSession = useRef({ committed: false });
  const [showSubunit, setShowSubunit] = useState(false);
  const [showSubunit2, setShowSubunit2] = useState(false);
  const [families, setFamilies] = useState([]);
  const [subfamilies, setSubfamilies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [consumptionFrequencies, setConsumptionFrequencies] = useState({});
  const [parStock, setParStock] = useState({ general: "", locations: {}, general_unit_id: "", location_units: {} });
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [savedItem, setSavedItem] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const saveProgress = useRef({ item: null, links: new Set() });
  const saveLock = useRef(false);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [subcategoryLoadFailed, setSubcategoryLoadFailed] = useState(false);
  const [optionsReload, setOptionsReload] = useState(0);
  const [saving, setSaving] = useState(false);
  const unitPolicy = useItemUnitCompatibility({ form, units, active: isOpen && canCreateItem && !loading && !loadFailed,
    original: editorAdapter?.assetContext?.catalog_record_id ? editorAdapter.initialItem : null,
    client: requestClient, requestOptions: editorAdapter ? undefined : requestOptions, corporate: Boolean(editorAdapter), languageId: userLanguageId });
  const [error, setErrorMessage] = useState("");
  const [errorNotice, setErrorNotice] = useState(null);
  const errorNoticeId = useRef(0);
  const errorNoticeFocus = useRef(null);
  const setError = useCallback((message) => {
    setErrorMessage(message);
    // A repeated validation error is a new notice, even when its text is unchanged.
    if (message) errorNoticeFocus.current = document.activeElement;
    setErrorNotice(message ? { id: ++errorNoticeId.current, message } : null);
  }, []);
  const dismissErrorNotice = useCallback(() => {
    if (modalRef.current?.querySelector(".aip-notice")?.contains(document.activeElement)
      && errorNoticeFocus.current?.isConnected) {
      errorNoticeFocus.current.focus({ preventScroll: true });
    }
    setErrorNotice(null);
  }, []);
  const [fieldPicker, setFieldPicker] = useState({ key: null, query: "" });
  const [supplierCreator, setSupplierCreator] = useState(null);

  useEffect(() => {
    supplierCreatorRef.current = null;
    supplierReturnFocusRef.current = null;
    setSupplierCreator(null);
    if (!isOpen) return;
    setDetailsTab("general"); detailsSession.current = { committed: false };
    const seed = editorAdapter?.initialItem || initialItem;
    setShowSubunit(Boolean(seed?.subunit_id));
    setShowSubunit2(Boolean(seed?.subunit_id && seed?.subunit2_id));
    setForm({ ...blankForm(lockedSupplierId), ...seed,
      minimum_purchase_quantity: seed?.minimum_purchase_quantity ?? "",
      purchase_multiple: seed?.purchase_multiple ?? "",
      pack_size: seed?.subunit_id ? seed.pack_size ?? "" : "",
      subunit2_id: seed?.subunit_id ? seed.subunit2_id || "" : "",
      subunit2_size: seed?.subunit_id && seed?.subunit2_id ? seed.subunit2_size ?? "" : "",
      suppliers: lockedSupplierId ? [String(lockedSupplierId)] : seed?.suppliers?.map((entry) => String(entry?.supplier_id ?? entry?.id ?? entry)) || [] });
    const par = seed?.par_stock;
    setParStock({ general: par?.general_selected_quantity ?? par?.general_par_quantity ?? "",
      general_unit_id: String(par?.general_unit_id || seed?.unit_id || ""),
      locations: Object.fromEntries((par?.location_par_levels || []).map((entry) => [entry.location_id, entry.selected_quantity ?? entry.par_quantity ?? ""])),
      location_units: Object.fromEntries((par?.location_par_levels || []).map((entry) => [entry.location_id, String(entry.unit_id || seed?.unit_id || "")])) });
    setConsumptionFrequencies(consumptionFrequencyDraft(seed?.consumption_frequencies || []));
    setSavedItem(null);
    setPendingRequest(null);
    saveProgress.current = { item: null, links: new Set() };
    saveLock.current = false;
    setSubcategories([]);
    setSaving(false);
    setError("");
    setFieldPicker({ key: null, query: "" });
  }, [isOpen, lockedSupplierId, editorAdapter, initialItem, setError]);

  useEffect(() => {
    if (!isOpen || !canCreateItem) return undefined;
    let cancelled = false;
    const stamp = access.capture();
    const isCurrent = () => Boolean(editorAdapter) || access.isCurrent(stamp, permission);

    const fetchAll = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const catalogClient = catalogReferenceClient(requestClient, languageId);
        const [catRes, unitRes, suppRes, familyRes, subfamilyRes, locationRes] = editorAdapter
          ? (await editorAdapter.loadOptions()).map((data) => ({ data }))
          : await Promise.all([
          catalogClient.get(`/api/purchasing/categories?business_unit_id=${businessUnitId}`, requestOptions()),
          requestClient.get(`/api/purchasing/units?business_unit_id=${businessUnitId}`, requestOptions()),
          catalogClient.get(`/api/purchasing/suppliers/options?business_unit_id=${businessUnitId}`, requestOptions()),
          catalogClient.get(`/api/purchasing/families?business_unit_id=${businessUnitId}`, requestOptions()),
          catalogClient.get(`/api/purchasing/subfamilies?business_unit_id=${businessUnitId}`, requestOptions()),
          requestClient.get("/api/purchasing/inventory/locations", requestOptions()),
        ]);

        if (cancelled || !isCurrent()) return;

        const sortedCategories = [...(catRes.data || [])].sort((a, b) => {
          const subA = String(a?.subfamily?.name || "").toLowerCase();
          const subB = String(b?.subfamily?.name || "").toLowerCase();
          if (subA !== subB) return subA.localeCompare(subB);
          return String(a?.name || "").toLowerCase().localeCompare(String(b?.name || "").toLowerCase());
        });

        const sortedSuppliers = [...(suppRes.data || [])].sort((a, b) =>
          String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
            sensitivity: "base",
          })
        );

        const unique = (rows) => [...new Map(rows.filter(Boolean).map((row) => [String(row.id), row])).values()];
        const loadedSubfamilies = subfamilyRes?.data || unique(sortedCategories.map((category) => category.subfamily));
        const loadedFamilies = familyRes?.data || unique(loadedSubfamilies.map((subfamily) => subfamily.family));
        setFamilies(loadedFamilies);
        setSubfamilies(loadedSubfamilies);
        setLocations((locationRes?.data || []).filter((location) => location.is_active !== false));
        setForm((previous) => {
          const category = sortedCategories.find((row) => String(row.id) === String(previous.category_id));
          const subfamilyId = category?.subfamily_id || category?.subfamily?.id || previous.subfamily_id;
          const subfamily = loadedSubfamilies.find((row) => String(row.id) === String(subfamilyId));
          return { ...previous, subfamily_id: String(subfamilyId || ""),
            family_id: String(subfamily?.family_id || subfamily?.family?.id || previous.family_id || "") };
        });
        setCategories(sortedCategories);
        setUnits(unitRes.data || []);
        setSuppliers(sortedSuppliers);
      } catch (err) {
        console.error("[AddItemPopup] Error fetching selectors:", err);
        if (!cancelled && isCurrent()) { setError("Error preparing item popup."); setLoadFailed(true); }
      } finally {
        if (!cancelled && isCurrent()) setLoading(false);
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [isOpen, editorAdapter, requestClient, setError, optionsReload, languageId, access, requestOptions, businessUnitId, canCreateItem]);

  useEffect(() => {
    if (!isOpen || !canCreateItem) return undefined;
    if (!form.category_id) {
      setSubcategories([]);
      setLoadingSubcategories(false);
      setSubcategoryLoadFailed(false);
      return undefined;
    }

    let cancelled = false;
    const stamp = access.capture();
    const isCurrent = () => Boolean(editorAdapter) || access.isCurrent(stamp, permission);
    setLoadingSubcategories(true);
    setSubcategoryLoadFailed(false);
    const fetchSubcategories = async () => {
      try {
        const res = editorAdapter ? { data: await editorAdapter.loadSubcategories(form.category_id) } : await catalogReferenceClient(requestClient, languageId).get(
          `/api/purchasing/subcategories?business_unit_id=${businessUnitId}&category_id=${form.category_id}`, requestOptions()
        );
        if (cancelled || !isCurrent()) return;

        const filtered = Array.isArray(res.data)
          ? res.data.filter((subcategory) => String(subcategory.category_id) === String(form.category_id))
          : [];

        filtered.sort((a, b) =>
          String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
            sensitivity: "base",
          })
        );

        setSubcategories((previous) => [...new Map([...filtered, ...previous.filter((row) => String(row.category_id) === String(form.category_id))].map((row) => [String(row.id), row])).values()]);
      } catch (err) {
        console.error("[AddItemPopup] Error fetching subcategories:", err);
        if (!cancelled && isCurrent()) { setSubcategories([]); setSubcategoryLoadFailed(true); }
      } finally {
        if (!cancelled && isCurrent()) setLoadingSubcategories(false);
      }
    };

    fetchSubcategories();
    return () => {
      cancelled = true;
    };
  }, [form.category_id, isOpen, editorAdapter, requestClient, optionsReload, languageId, access, requestOptions, businessUnitId, canCreateItem]);

  const selectedSupplierIds = useMemo(
    () =>
      (form.suppliers || [])
        .map((id) => Number(id))
        .filter((id) => Number.isSafeInteger(id) && id !== 0),
    [form.suppliers]
  );

  const selectedSuppliers = useMemo(
    () =>
      selectedSupplierIds.map((id) => suppliers.find((supplier) => Number(supplier.id) === id) || { id, name: `#${id}` }),
    [selectedSupplierIds, suppliers]
  );

  const availableSuppliers = useMemo(
    () => suppliers.filter((supplier) => !selectedSupplierIds.includes(Number(supplier.id))),
    [selectedSupplierIds, suppliers]
  );

  const lockedSupplier = useMemo(
    () => suppliers.find((supplier) => Number(supplier.id) === lockedSupplierId) || null,
    [lockedSupplierId, suppliers]
  );

  const sortedUnitOptions = useMemo(
    () => units
      .map((unit) => ({ ...makeOption(unit.id, getUnitName(unit, userLanguageId) || `${t("Unit")} #${unit.id}`), searchText: unitSearchText(unit).toLowerCase() }))
      .sort((left, right) => left.label.localeCompare(right.label, userLanguageId, { sensitivity: "base", numeric: true })),
    [units, t, userLanguageId]
  );

  const unitOptions = useMemo(
    () => [
      makeOption("", t("Select unit"), t("Required")),
      ...sortedUnitOptions,
    ],
    [sortedUnitOptions, t]
  );

  const optionalUnitOptions = useMemo(
    () => [
      makeOption("", t("None"), t("Optional")),
      ...sortedUnitOptions,
    ],
    [sortedUnitOptions, t]
  );

  const selectedCategory = useMemo(
    () => {
      const category = form.category?.id && String(form.category.id) === String(form.category_id) ? form.category : categories.find((row) => String(row.id) === String(form.category_id));
      if (!category) return null;
      const subfamily = subfamilies.find((row) => String(row.id) === String(category.subfamily_id || category.subfamily?.id)) || category.subfamily;
      const family = families.find((row) => String(row.id) === String(subfamily?.family_id || subfamily?.family?.id)) || subfamily?.family;
      return { ...category, subfamily: subfamily ? { ...subfamily, family } : null };
    },
    [categories, subfamilies, families, form.category_id, form.category]
  );

  const selectedSubcategory = useMemo(
    () => (String(form.subcategory?.id) === String(form.subcategory_id) ? form.subcategory : null) || subcategories.find((subcategory) => String(subcategory.id) === String(form.subcategory_id)) || null,
    [form.subcategory_id, form.subcategory, subcategories]
  );

  const frequencyUnits = consumptionUnits(form, units);
  const parUnits = parStockUnits(form, units);
  const classificationUnresolved = Boolean((form.category_id && !selectedCategory)
    || (form.subcategory_id && (!selectedSubcategory || String(selectedSubcategory.category_id) !== String(form.category_id))));
  const isDirectConsumption = isDirectConsumptionItem({
    inventory_policy: form.inventory_policy,
    category: selectedCategory,
    subcategory: selectedSubcategory
      ? { ...selectedSubcategory, category: selectedCategory }
      : null,
  });

  useEffect(() => {
    if (!loading && !loadingSubcategories && !classificationUnresolved && selectedCategory && isDirectConsumption) {
      setParStock({ general: "", locations: {}, general_unit_id: "", location_units: {} });
    }
  }, [isDirectConsumption, loading, loadingSubcategories, classificationUnresolved, selectedCategory]);

  const inventoryPolicyOptions = useMemo(
    () =>
      inventoryPolicySelectOptionsForItem({
        category: selectedCategory,
        subcategory: selectedSubcategory
          ? {
              ...selectedSubcategory,
              category: selectedSubcategory.category || selectedCategory,
            }
          : null,
      }).map((option) => makeOption(option.value, t(option.label))),
    [selectedCategory, selectedSubcategory, t]
  );

  const requisitionSupplierOptions = useMemo(
    () => [
      makeOption("", t("Select supplier"), t("Required")),
      ...suppliers.map((supplier) =>
        makeOption(
          supplier.id,
          supplier.name || `${t("Supplier")} #${supplier.id}`,
          supplier.code ? `${t("Code")}: ${supplier.code}` : ""
        )
      ),
    ],
    [suppliers, t]
  );

  const addableSupplierOptions = useMemo(
    () =>
      availableSuppliers.map((supplier) =>
        makeOption(
          supplier.id,
          supplier.name || `${t("Supplier")} #${supplier.id}`,
          supplier.code ? `${t("Code")}: ${supplier.code}` : ""
        )
      ),
    [availableSuppliers, t]
  );

  const pickerConfigs = useMemo(
    () => ({
      unit_id: {
        title: "Select Unit",
        hint: "Pick the base unit for this item.",
        options: unitOptions,
        emptyText: "No units available.",
      },
      subunit_id: {
        title: "Select Subunit",
        hint: "Optional secondary unit.",
        options: optionalUnitOptions,
        emptyText: "No units available.",
      },
      subunit2_id: {
        title: "Select Subunit 2",
        hint: "Optional tertiary unit.",
        options: optionalUnitOptions,
        emptyText: "No units available.",
      },
      inventory_policy: {
        title: "Select Inventory Policy",
        hint: "Inherit from the hierarchy or override this specific item.",
        options: inventoryPolicyOptions,
        emptyText: "No inventory policies available.",
      },
      assigned_supplier: {
        title: "Select Supplier",
        hint: "Choose the supplier for this requisition item.",
        options: requisitionSupplierOptions,
        emptyText: "No suppliers available.",
      },
      add_supplier: {
        title: "Add Supplier",
        hint: "Pick a supplier to link to this item.",
        options: addableSupplierOptions,
        emptyText: suppliers.length ? "All suppliers are already linked." : "No suppliers available.",
        disabled: !addableSupplierOptions.length && !canCreateSupplier,
      },
    }),
    [addableSupplierOptions, canCreateSupplier, inventoryPolicyOptions, optionalUnitOptions, requisitionSupplierOptions, suppliers.length, unitOptions]
  );

  const currentPicker = fieldPicker.key ? pickerConfigs[fieldPicker.key] : null;

  const filteredPickerOptions = useMemo(() => {
    if (!currentPicker) return [];
    const query = String(fieldPicker.query || "").trim().toLowerCase();
    if (!query) return currentPicker.options;
    return currentPicker.options.filter((option) => option.searchText.includes(query));
  }, [currentPicker, fieldPicker.query]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category_id" ? { subcategory_id: "" } : {}),
    }));
  };

  const addSupplier = (supplierId) => {
    const parsedId = Number(supplierId);
    if (!parsedId) return;
    setForm((prev) => {
      const current = (prev.suppliers || []).map((id) => String(id));
      const nextId = String(parsedId);
      return {
        ...prev,
        suppliers: current.includes(nextId) ? current : [...current, nextId],
      };
    });
  };

  const removeSupplier = (supplierId) => {
    if (lockedSupplierId) return;
    setForm((prev) => ({
      ...prev,
      suppliers: (prev.suppliers || []).filter((id) => Number(id) !== Number(supplierId)),
    }));
  };

  const handleSingleSupplierChange = (supplierId) => {
    const parsedId = Number(supplierId);
    setForm((prev) => ({
      ...prev,
      suppliers: parsedId ? [String(parsedId)] : [],
    }));
  };

  const closePopup = () => {
    if (saving || supplierCreator) return;
    if (typeof onClose === "function") onClose();
  };

  const closePicker = () => {
    setFieldPicker({ key: null, query: "" });
    pickerTriggerRef.current?.focus();
  };

  dismissRef.current = () => {
    if (saving || supplierCreator) return;
    if (fieldPicker.key) closePicker();
    else if (errorNotice) dismissErrorNotice();
    else closePopup();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    modalRef.current?.querySelector("input")?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        dismissRef.current?.();
      } else if (event.key === "Tab") {
        const surface = pickerRef.current || modalRef.current;
        const controls = [...(surface?.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') || [])];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || !surface?.contains(document.activeElement))) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !surface?.contains(document.activeElement))) {
          event.preventDefault(); first?.focus();
        }
      }
    };
    const surface = modalRef.current?.parentElement;
    surface?.addEventListener("keydown", handleKeyDown);
    return () => {
      surface?.removeEventListener("keydown", handleKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !loading && !loadFailed) modalRef.current?.querySelector("input")?.focus();
  }, [isOpen, loading, loadFailed]);

  useEffect(() => {
    if (!supplierCreator && supplierReturnFocusRef.current) {
      supplierReturnFocusRef.current.focus();
      supplierReturnFocusRef.current = null;
    }
  }, [supplierCreator]);

  const openSupplierCreator = () => {
    if (!canCreateSupplier || saving || loading || loadFailed || savedItem) return;
    const creation = { businessUnitId, pickerKey: fieldPicker.key };
    supplierCreatorRef.current = creation;
    setSupplierCreator(creation);
  };

  const supplierAdded = (supplier) => {
    if (supplierCreatorRef.current !== supplierCreator || !supplier?.id) return;
    setSuppliers((previous) => [...previous.filter((entry) => Number(entry.id) !== Number(supplier.id)), supplier]
      .sort((left, right) => String(left.name || "").localeCompare(String(right.name || ""), undefined, { sensitivity: "base" })));
    if (supplierCreator.pickerKey === "assigned_supplier") handleSingleSupplierChange(supplier.id);
    else addSupplier(supplier.id);
    setFieldPicker({ key: null, query: "" });
    supplierReturnFocusRef.current = pickerTriggerRef.current;
  };

  const closeSupplierCreator = () => {
    if (supplierCreatorRef.current !== supplierCreator) return;
    supplierCreatorRef.current = null;
    setSupplierCreator(null);
  };

  const openPicker = (key) => {
    const config = pickerConfigs[key];
    if (!config || config.disabled) return;
    dismissErrorNotice();
    pickerTriggerRef.current = document.activeElement;
    setFieldPicker({ key, query: "" });
  };

  const removeSubunit = (level) => {
    setForm((prev) => ({ ...prev,
      ...(level === 1 ? { subunit_id: "", pack_size: "" } : {}),
      subunit2_id: "", subunit2_size: "",
    }));
    if (level === 1) setShowSubunit(false);
    setShowSubunit2(false);
  };

  const applyPickerSelection = (optionValue) => {
    if (!fieldPicker.key) return;

    switch (fieldPicker.key) {
      case "unit_id":
        setForm((prev) => ({ ...prev, unit_id: String(optionValue || "") }));
        if (!optionValue) removeSubunit(1);
        break;
      case "subunit_id":
        if (!optionValue) removeSubunit(1);
        else setForm((prev) => ({ ...prev, subunit_id: String(optionValue) }));
        break;
      case "subunit2_id":
        if (!optionValue) removeSubunit(2);
        else setForm((prev) => ({ ...prev, subunit2_id: String(optionValue) }));
        break;
      case "inventory_policy":
        setForm((prev) => ({ ...prev, inventory_policy: String(optionValue || "") }));
        break;
      case "assigned_supplier":
        handleSingleSupplierChange(optionValue);
        break;
      case "add_supplier":
        addSupplier(optionValue);
        break;
      default:
        break;
    }

    closePicker();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (detailsBusy || saveLock.current || saving || loading || loadingSubcategories || loadFailed || classificationUnresolved || (!editorAdapter && !access.can(permission))) return;
    const stamp = access.capture();
    const isCurrent = () => Boolean(editorAdapter) || access.isCurrent(stamp, permission);
    setDetailsTab("general");

    const internalCode = String(form.code || "").trim();
    if (!internalCode || internalCode.length > 50) {
      setError(internalCode ? `The internal code contains ${internalCode.length} characters. The maximum is 50.` : "Enter the internal code before saving.");
      return;
    }
    if (!String(form.name || "").trim()) {
      setError("Enter the name before saving.");
      return;
    }

    if (!savedItem && hasInternalCodeConflict(form.code, existingRecords)) {
      setError(duplicateInternalCodeMessage(form.code));
      return;
    }

    if (!form.unit_id) {
      setError("Please select a unit.");
      return;
    }

    if (!form.category_id) {
      setError("Please select a category.");
      return;
    }

    if (!selectedSupplierIds.length) {
      setError("Select at least one supplier.");
      return;
    }

    if (unitPolicy.problem) { setError(unitPolicy.problem); return; }

    let purchaseConstraints;
    try { purchaseConstraints = serializePurchaseConstraints(form, t); }
    catch (validationError) { setError(validationError.message); return; }

    const planningLocations = approvalRequest ? [...new Map([
      ...(initialItem?.par_stock?.location_par_levels || []).map((row) => ({ id: row.location_id })),
      ...(initialItem?.consumption_frequencies || []).map((row) => ({ id: row.location_id })), ...locations,
    ].map((row) => [String(row.id), row])).values()] : locations;
    let frequencyPayload;
    if (!editorAdapter && isDirectConsumption) {
      try { frequencyPayload = serializeConsumptionFrequencies(consumptionFrequencies, planningLocations, frequencyUnits, form.unit_id, t); }
      catch (validationError) { setError(validationError.message); return; }
    }
    let parPayload;
    if (!editorAdapter && !isDirectConsumption) {
      try {
        const payload = serializeParStock(
          { quantity: parStock.general, unit_id: parStock.general_unit_id },
          planningLocations.map((location) => ({ location_id: location.id, quantity: parStock.locations[location.id], unit_id: parStock.location_units[location.id] })),
          parUnits, form.unit_id, t
        );
        if (payload.general_par_quantity !== null || payload.location_par_levels.length) parPayload = payload;
      } catch (validationError) {
        setError(validationError.message);
        return;
      }
    }

    saveLock.current = true;
    setSaving(true);
    setError("");

    try {
      const clean = (value) => (value === "" ? null : value);

      const payload = {
        code: internalCode,
        supplier_code: clean(String(form.supplier_code || "").trim()),
        name: String(form.name || "").trim(),
        description: clean(String(form.description || "").trim()),
        product_details: form.product_details,
        category_id: clean(form.category_id),
        subcategory_id: clean(form.subcategory_id),
        ...(inventoryPolicyEnabled ? { inventory_policy: clean(form.inventory_policy) } : {}),
        unit_id: clean(form.unit_id),
        subunit_id: clean(form.subunit_id),
        pack_size: form.subunit_id ? clean(form.pack_size) : null,
        subunit2_id: form.subunit_id ? clean(form.subunit2_id) : null,
        ...unitPolicy.fields,
        subunit2_size: form.subunit_id && form.subunit2_id ? clean(form.subunit2_size) : null,
        ...purchaseConstraints,
        ...(frequencyPayload !== undefined ? { consumption_frequencies: frequencyPayload } : {}),
      };

      let newItem;
      if (approvalRequest) {
        newItem = await approvalRequest.save({ ...payload,
          suppliers: selectedSupplierIds.map((supplier_id) => {
            const original = initialItem?.suppliers?.find((entry) => Number(entry?.supplier_id ?? entry?.id ?? entry) === supplier_id);
            return { ...(original && typeof original === "object" ? original : {}), supplier_id };
          }),
          par_stock: { ...(initialItem?.par_stock || {}), general_par_quantity: parPayload?.general_par_quantity ?? null,
            general_selected_quantity: parPayload?.general_selected_quantity ?? parPayload?.general_par_quantity ?? null, general_unit_id: parPayload?.general_unit_id ?? Number(form.unit_id),
            location_par_levels: (parPayload?.location_par_levels || []).map((entry) => ({
              ...initialItem?.par_stock?.location_par_levels?.find((original) => Number(original.location_id) === Number(entry.location_id)),
              ...entry, selected_quantity: entry.selected_quantity ?? entry.par_quantity ?? null, unit_id: entry.unit_id ?? Number(form.unit_id),
            })),
          },
          consumption_frequencies: (frequencyPayload || []).map((entry) => ({
            ...initialItem?.consumption_frequencies?.find((original) => Number(original.location_id) === Number(entry.location_id)), ...entry,
          })),
        });
      } else if (editorAdapter) {
        newItem = await editorAdapter.saveItem(payload, selectedSupplierIds);
      } else {
        // Keep completed steps when a later request fails so retrying cannot create a duplicate item.
        newItem = saveProgress.current.item;
        if (!newItem) {
          const itemRes = await requestClient.post("/api/purchasing/items", { ...payload,
            suppliers: selectedSupplierIds.map((supplier_id) => ({ supplier_id,
              ...(lockedSupplierId ? { supplier_code: payload.supplier_code } : {}),
              is_preferred: lockedSupplierId ? supplier_id === lockedSupplierId : false })),
            ...(parPayload ? { par_stock: parPayload } : {}),
          }, access.requestConfig(permission));
          if (!isCurrent()) return;
          if (isPendingCatalogCreation(itemRes.data)) { detailsSession.current.committed = true; setPendingRequest(itemRes.data.request); return; }
          newItem = { ...payload, ...itemRes.data };
          saveProgress.current.item = newItem;
          setSavedItem(newItem);
          if (newItem.catalog_associations_saved) selectedSupplierIds.forEach((id) => saveProgress.current.links.add(id));
        }
        for (const supplierId of selectedSupplierIds) {
          if (saveProgress.current.links.has(supplierId)) continue;
          await requestClient.post("/api/purchasing/item-suppliers", {
            item_id: newItem.id,
            supplier_id: supplierId,
            ...(lockedSupplierId ? { supplier_code: payload.supplier_code } : {}),
            is_preferred: lockedSupplierId ? supplierId === lockedSupplierId : false,
          }, access.requestConfig(permission));
          if (!isCurrent()) return;
          saveProgress.current.links.add(supplierId);
        }
        if (parPayload && !newItem.catalog_associations_saved) await requestClient.put(`/api/purchasing/inventory/par-levels/item/${newItem.id}`, parPayload, access.requestConfig(permission));
        newItem = { ...newItem, category: selectedCategory, subcategory: selectedSubcategory,
          unit: units.find((unit) => String(unit.id) === String(form.unit_id)),
          subunit: units.find((unit) => String(unit.id) === String(form.subunit_id)),
          subunit2: units.find((unit) => String(unit.id) === String(form.subunit2_id)),
          item_suppliers: selectedSupplierIds.map((supplier_id) => ({ supplier_id, item_id: newItem.id })) };

      }

      if (!isCurrent()) return;
      detailsSession.current.committed = true;
      if (typeof onAdded === "function") {
        await onAdded(newItem);
      }

      closePopup();
    } catch (err) {
      if (!isCurrent()) return;
      console.error("[AddItemPopup] Save error:", err);
      if (String(err.response?.data?.code || "").startsWith("PRODUCT_")) setDetailsTab("details");
      setError(catalogRequestError(err, "Error saving item.", form.code));
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  };

  const renderSelectControl = ({
    label,
    name,
    value,
    options,
    pickerKey = name,
    required = false,
    disabled = false,
  }) => {
    const selected = getSelectedOption(options, value);


    return (
      <>
        <label className="aip-label" htmlFor={`${dialogId}-${name}`}>{t(label)}{required ? " *" : ""}</label>
        <button
          id={`${dialogId}-${name}`}
          type="button"
          aria-haspopup="dialog"
          className={`aip-selectTrigger ${disabled ? "aip-selectTrigger--disabled" : ""}`}
          onClick={() => openPicker(pickerKey)}
          disabled={disabled}
        >
          <span className={`aip-selectValue ${!selected || selected.value === "" ? "aip-selectValue--placeholder" : ""}`}>
            {selected?.label || `${t("Select")} ${t(label).toLowerCase()}`}
          </span>
          {selected?.meta ? <span className="aip-selectMeta">{selected.meta}</span> : <span className="aip-selectMeta">{t("Browse options in a full picker")}</span>}
          <span className="aip-selectAction">{t("Browse")}</span>
        </button>
      </>
    );
  };

  if (!isOpen || !canCreateItem) return null;
  if (pendingRequest) return <CatalogCreationPendingNotice request={pendingRequest} onClose={closePopup} />;

  return (
    <div className="aip-wrap pilcore-purchasing-overlay" data-theme={themeId}>
      <div className="aip-modal" ref={modalRef} role="dialog" aria-modal={!isSplit} aria-labelledby={`${dialogId}-title`} aria-hidden={currentPicker || supplierCreator ? true : undefined} inert={supplierCreator ? "" : undefined}>
        <div className="aip-head">
          <div className="aip-headMain">
            <h5 className="aip-title" id={`${dialogId}-title`}>{approvalRequest?.title || t(editorAdapter?.title || (isMobile ? "New Item" : "New Catalog Item"))}</h5>
            <div className="aip-sub">
              {approvalRequest?.hint || (editorAdapter?.hint ? t(editorAdapter.hint) : lockedSupplierId
                ? `${t("Catalog workspace in locked supplier mode")}${lockedSupplier ? ` — ${lockedSupplier.name}` : ""}`
                : isRequisitionContext
                ? t("Create a new catalog item and assign one supplier for this requisition.")
                : t("Create a catalog item and link it to one or more suppliers without leaving Items & Families."))}
            </div>
          </div>
          <div className="aip-headActions">
            <span className="aip-badge">
              {t(lockedSupplierId ? "LOCKED" : isRequisitionContext ? "REQUISITION" : "CATALOG")}
            </span>
            <button
              type="button"
              className="aip-closeBtn"
              aria-label={t(editorAdapter ? "Close" : "Close add item popup")}
              title={t("Close")}
              disabled={saving}
              onClick={closePopup}
            >
              {isMobile ? t("Close") : "×"}
            </button>
          </div>
        </div>

        <form className="aip-form" onSubmit={handleSubmit} onInvalidCapture={(event) => revealItemInvalid(event, setDetailsTab)}>
          <div className="aip-body">
            <ItemDetailsTabs id={dialogId} active={detailsTab} onChange={setDetailsTab} languageId={languageId} />
            <div id={`${dialogId}-general-panel`} role="tabpanel" aria-labelledby={`${dialogId}-general-tab`} hidden={detailsTab !== "general"}>
            <fieldset className="aip-formStack aip-fieldset" disabled={!canCreateItem || saving || loading || loadFailed || Boolean(savedItem)}>
              <div className="aip-row aip-row--one">
                <div className="aip-field">
                  <label className="aip-label" htmlFor={`${dialogId}-name`}>{t("Name")} *</label>
                  <input
                    id={`${dialogId}-name`}
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="form-control"
                  />
                </div>
              </div>

              <div className="aip-row aip-row--two">
                <div className="aip-field">
                  <label className="aip-label" htmlFor={`${dialogId}-code`}>{t("Internal Code")} *</label>
                  <input
                    id={`${dialogId}-code`}
                    type="text"
                    name="code"
                    value={form.code}
                    onChange={handleChange}
                    required
                    className="form-control"
                  />
                  {!editorAdapter && <InternalCodeSuggestions name={form.name} value={form.code} resource="items" languageId={languageId} requestClient={requestClient} excludeRequestId={approvalRequest?.id}
                    disabled={!canCreateItem || saving || loading || loadFailed || Boolean(savedItem)}
                    onSuggest={(code) => { setForm((previous) => ({ ...previous, code })); setError(""); }} />}
                </div>

                <div className="aip-field">
                  <label className="aip-label" htmlFor={`${dialogId}-supplier-code`}>{t("Supplier Item Code")}</label>
                  <input
                    id={`${dialogId}-supplier-code`}
                    type="text"
                    name="supplier_code"
                    value={form.supplier_code}
                    onChange={handleChange}
                    className="form-control"
                    placeholder={t("Optional supplier reference")}
                  />
                </div>
              </div>

              <ItemClassificationPicker allowPending={!editorAdapter} value={{ ...form, category: selectedCategory,
                subcategory: selectedSubcategory ? { ...selectedSubcategory, category: selectedCategory } : null }} adapter={editorAdapter} requestClient={requestClient} languageId={languageId}
                suggestionEnabled={canSuggestClassification}
                disabled={!canCreateItem || saving || loading || loadFailed || Boolean(savedItem)}
                onChange={(classification) => setForm((previous) => ({ ...previous, ...classification }))} />

              <div className="aip-row aip-row--one">
                <div className="aip-field">
                  <label className="aip-label" htmlFor={`${dialogId}-description`}>{t("Description")}</label>
                  <textarea id={`${dialogId}-description`} name="description" rows={3} value={form.description || ""}
                    onChange={handleChange} className="form-control" />
                </div>
              </div>

              {inventoryPolicyEnabled && <div className="aip-row aip-row--one">
                <div className="aip-field">
                  {renderSelectControl({
                    label: "Inventory Policy",
                    name: "inventory_policy",
                    value: form.inventory_policy,
                    options: inventoryPolicyOptions,
                  })}
                  <div className="aip-fieldHint">
                    {t("Item policy overrides its subcategory, category, subfamily and family. Choose Inherit to follow the hierarchy.")}
                  </div>
                </div>
              </div>}

              <div className="aip-row item-purchase-fields">
                <div className="aip-field">
                  {renderSelectControl({ label: "Unit", name: "unit_id", value: form.unit_id, options: unitOptions, required: true })}
                </div>
                {purchaseConstraintFields.map(({ name, label, hint }) => <div className="aip-field" key={name}>
                  <label className="aip-label" htmlFor={`${dialogId}-${name}`}>{t(label)}</label>
                  <input id={`${dialogId}-${name}`} name={name} type="number" min="0.0001" max="9999999999.9999" step="0.0001"
                    value={form[name]} onChange={handleChange} className="form-control" placeholder={t("No restriction")}
                    aria-describedby={`${dialogId}-${name}-hint`} />
                  <div className="aip-fieldHint" id={`${dialogId}-${name}-hint`}>{t(hint)}</div>
                </div>)}
              </div>

              {form.unit_id && (!showSubunit ? <button type="button" className="aip-btn aip-btn--ghost" onClick={() => setShowSubunit(true)}>{t("Add subunit")}</button> :
                <div className={`aip-row item-subunit-row${form.subunit_id ? "" : " item-subunit-row--empty"}`}>
                  <div className="aip-field">
                    {renderSelectControl({ label: "Subunit", name: "subunit_id", value: form.subunit_id, options: optionalUnitOptions })}
                    {form.subunit2_id && <div className="aip-fieldHint">{t("Removing this subunit also removes subunit 2.")}</div>}
                  </div>
                  <div className="aip-field item-subunit-packField">
                    {form.subunit_id && <label className="aip-label" htmlFor={`${dialogId}-pack`}>{t("Pack")}</label>}
                    <div className="item-subunit-packControl">
                      {form.subunit_id && <input id={`${dialogId}-pack`} type="number" step="0.01" name="pack_size"
                        value={form.pack_size} onChange={handleChange} className="form-control" />}
                      <button type="button" className="item-subunit-remove" aria-label={t("Remove subunit")}
                        title={t("Remove subunit")} onClick={() => removeSubunit(1)}><FiTrash2 aria-hidden="true" /></button>
                    </div>
                  </div>
                  <ItemSubunitPreview unitId={form.unit_id} subunitId={form.subunit_id} factor={form.pack_size}
                    itemName={form.name} units={units} languageId={userLanguageId} />
                </div>)}

              {form.unit_id && form.subunit_id && (!showSubunit2 ? <button type="button" className="aip-btn aip-btn--ghost" onClick={() => setShowSubunit2(true)}>{t("Add subunit 2")}</button> :
                <div className={`aip-row item-subunit-row${form.subunit2_id ? "" : " item-subunit-row--empty"}`}>
                  <div className="aip-field">
                    {renderSelectControl({ label: "Subunit 2", name: "subunit2_id", value: form.subunit2_id, options: optionalUnitOptions })}
                  </div>
                  <div className="aip-field item-subunit-packField">
                    {form.subunit2_id && <label className="aip-label" htmlFor={`${dialogId}-pack2`}>{t("Pack 2")}</label>}
                    <div className="item-subunit-packControl">
                      {form.subunit2_id && <input id={`${dialogId}-pack2`} type="number" step="0.01" name="subunit2_size"
                        value={form.subunit2_size} onChange={handleChange} className="form-control" />}
                      <button type="button" className="item-subunit-remove" aria-label={t("Remove subunit 2")}
                        title={t("Remove subunit 2")} onClick={() => removeSubunit(2)}><FiTrash2 aria-hidden="true" /></button>
                    </div>
                  </div>
                  <ItemSubunitPreview unitId={form.subunit_id} subunitId={form.subunit2_id} factor={form.subunit2_size}
                    itemName={form.name} units={units} languageId={userLanguageId} />
                </div>)}
              <ItemUnitCompatibility policy={unitPolicy} setForm={setForm} />
            </fieldset>

            <fieldset className="aip-panel aip-fieldset" disabled={!canCreateItem || saving || loading || loadFailed || Boolean(savedItem)}>
              <div className="aip-panelHead">
                <div>
                  <h6>{t("Suppliers")}</h6>
                  <p>
                    {lockedSupplierId
                      ? t("This item will be linked automatically to the locked supplier.")
                      : isRequisitionContext
                      ? t("Choose one supplier for this requisition flow.")
                      : t("Add one or more suppliers that can provide this item.")}
                  </p>
                </div>
              </div>

              {lockedSupplierId ? (
                <div className="aip-lockCard">
                  <div>
                    <strong>{lockedSupplier?.name || `${t("Supplier")} #${lockedSupplierId}`}</strong>
                    <small>{t("Locked supplier")}</small>
                  </div>
                  <span className="aip-badge">{t("AUTO LINKED")}</span>
                </div>
              ) : isRequisitionContext ? (
                <div className="aip-supplierPanel">
                  <div className="aip-field">

                      <>
                        <label className="aip-label" htmlFor={`${dialogId}-supplier`}>{t("Assigned Supplier")} *</label>
                        <button
                          id={`${dialogId}-supplier`}
                          type="button"
                          aria-haspopup="dialog"
                          className="aip-selectTrigger"
                          onClick={() => openPicker("assigned_supplier")}
                        >
                          <span className={`aip-selectValue ${!selectedSupplierIds[0] ? "aip-selectValue--placeholder" : ""}`}>
                            {getSelectedOption(requisitionSupplierOptions, selectedSupplierIds[0] || "", t("Select supplier"))?.label || t("Select supplier")}
                          </span>
                          <span className="aip-selectMeta">
                            {getSelectedOption(requisitionSupplierOptions, selectedSupplierIds[0] || "")?.meta || t("Browse suppliers in a full picker")}
                          </span>
                          <span className="aip-selectAction">{t("Browse")}</span>
                        </button>
                      </>

                  </div>

                  <div className="aip-chipList">
                    {selectedSuppliers.length ? (
                      selectedSuppliers.map((supplier) => (
                        <span key={supplier.id} className="aip-chip">
                          {supplier.name}
                          <button
                            type="button"
                            className="aip-chipRemove"
                            onClick={() => handleSingleSupplierChange("")}
                            title={t("Remove supplier")}
                            aria-label={`${t("Remove supplier")}: ${supplier.name}`}
                          >
                            x
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="aip-empty">{t("No supplier selected yet.")}</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="aip-supplierPanel">
                  <div className="aip-field">

                      <>
                        <label className="aip-label" htmlFor={`${dialogId}-supplier`}>{t("Add Supplier")} *</label>
                        <button
                          id={`${dialogId}-supplier`}
                          type="button"
                          aria-haspopup="dialog"
                          className={`aip-selectTrigger ${!addableSupplierOptions.length && !canCreateSupplier ? "aip-selectTrigger--disabled" : ""}`}
                          onClick={() => openPicker("add_supplier")}
                          disabled={!addableSupplierOptions.length && !canCreateSupplier}
                        >
                          <span className={`aip-selectValue ${!addableSupplierOptions.length ? "aip-selectValue--placeholder" : ""}`}>
                            {t(!addableSupplierOptions.length && !canCreateSupplier ? "All suppliers linked" : "Add supplier")}
                          </span>
                          <span className="aip-selectMeta">
                            {!addableSupplierOptions.length
                              ? t(canCreateSupplier ? "Create a supplier without leaving this item." : "There are no more suppliers available to add.")
                              : t("Browse available suppliers in a full picker")}
                          </span>
                          <span className="aip-selectAction">{t("Browse")}</span>
                        </button>
                      </>

                  </div>

                  <div className="aip-chipList">
                    {selectedSuppliers.length ? (
                      selectedSuppliers.map((supplier) => (
                        <span key={supplier.id} className="aip-chip">
                          {supplier.name}
                          <button
                            type="button"
                            className="aip-chipRemove"
                            onClick={() => removeSupplier(supplier.id)}
                            title={t("Remove supplier")}
                            aria-label={`${t("Remove supplier")}: ${supplier.name}`}
                          >
                            x
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="aip-empty">{t("No suppliers selected yet.")}</span>
                    )}
                  </div>
                </div>
              )}
            </fieldset>

            {!editorAdapter && isDirectConsumption && <ItemConsumptionFrequencies collapsible locations={locations} value={consumptionFrequencies}
              onChange={setConsumptionFrequencies} units={frequencyUnits} baseUnitId={form.unit_id} languageId={languageId} t={t}
              disabled={!canCreateItem || saving || loading || loadFailed || Boolean(savedItem)} />}
            {!editorAdapter && !isDirectConsumption && <details className="aip-panel aip-disclosure" aria-label={t("Par Stock")}>
              <summary className="aip-panelHead"><h6>{t("Par Stock")}</h6></summary>
              <div className="aip-panelHead"><p>{t("Set each target using one of the item's units, by location and across all locations.")}</p></div>
              <div className="aip-row aip-row--two">
                <div className="aip-field"><label className="aip-label" htmlFor={`${dialogId}-general-par`}>{t("General Par Stock")}</label>
                  <div className="item-par-quantity">
                    <input id={`${dialogId}-general-par`} name="general_par_quantity" type="number" min="0.00000001" step="0.00000001" className="form-control"
                      value={parStock.general} disabled={!canCreateItem || saving || loading || loadFailed} onChange={(event) => setParStock((prev) => ({ ...prev, general: event.target.value, general_unit_id: prev.general_unit_id || String(form.unit_id) }))} />
                    <ItemParUnitSelect value={parStock.general_unit_id} baseUnitId={form.unit_id} units={parUnits}
                      label={`${t("Par stock unit")} · ${t("General")}`} languageId={languageId} t={t} disabled={!canCreateItem || saving || loading || loadFailed}
                      onChange={(unitId) => setParStock((prev) => ({ ...prev, general_unit_id: unitId }))} />
                  </div></div>
                {locations.map((location) => <div className="aip-field" key={location.id}>
                  <label className="aip-label" htmlFor={`${dialogId}-par-${location.id}`}>{t("Par Stock")} · {location.name}</label>
                  <div className="item-par-quantity">
                    <input id={`${dialogId}-par-${location.id}`} name={`par-${location.id}`} type="number" min="0.00000001" step="0.00000001" className="form-control"
                      value={parStock.locations[location.id] || ""} disabled={!canCreateItem || saving || loading || loadFailed}
                      onChange={(event) => setParStock((prev) => ({ ...prev, locations: { ...prev.locations, [location.id]: event.target.value }, location_units: { ...prev.location_units, [location.id]: prev.location_units[location.id] || String(form.unit_id) } }))} />
                    <ItemParUnitSelect value={parStock.location_units[location.id]} baseUnitId={form.unit_id} units={parUnits}
                      label={`${t("Par stock unit")} · ${location.name}`} languageId={languageId} t={t} disabled={!canCreateItem || saving || loading || loadFailed}
                      onChange={(unitId) => setParStock((prev) => ({ ...prev, location_units: { ...prev.location_units, [location.id]: unitId } }))} />
                  </div>
                </div>)}
              </div>
            </details>}
            {(loading || loadingSubcategories) && <p role="status">{t("Loading item options...")}</p>}
            {!loading && !loadingSubcategories && (loadFailed || subcategoryLoadFailed || classificationUnresolved) && <div>
              <p role="alert">{t("Error preparing item popup.")}</p>
              <button type="button" className="aip-btn" onClick={() => setOptionsReload(value => value + 1)}>{t("Retry")}</button>
            </div>}
            {savedItem && error && <p role="status">{t("Item created. Retry to finish supplier links and par stocks without creating it again.")}</p>}
            </div>
            <div id={`${dialogId}-details-panel`} role="tabpanel" aria-labelledby={`${dialogId}-details-tab`} hidden={detailsTab !== "details"}>
              <ProductDetailsFields value={form.product_details} onChange={(product_details) => setForm((previous) => ({ ...previous, product_details }))}
                languageId={languageId} active={detailsTab === "details"} disabled={!canCreateItem || saving || loading || loadFailed || Boolean(savedItem)}
                context={editorAdapter?.assetContext || { scope: "local", ...(approvalRequest?.id ? { request_id: approvalRequest.id } : {}) }}
                requestConfig={editorAdapter ? undefined : access.requestConfig(permission)} onBusy={setDetailsBusy} session={detailsSession} />
            </div>
          </div>

          <div className="aip-actions">
              <button type="button" className="aip-btn" disabled={saving} onClick={closePopup}>
                {t("Cancel")}
              </button>
              <button type="submit" className="aip-btn aip-btn--primary" disabled={detailsBusy || !canCreateItem || saving || loading || loadingSubcategories || loadFailed || classificationUnresolved}>
                {saving ? t("Saving...") : approvalRequest?.submitLabel || t("Save Item")}
              </button>
          </div>
        </form>
        {errorNotice && <ItemFormNotice key={errorNotice.id}
          message={t(errorNotice.message)}
          detail={savedItem ? t("Item created. Retry to finish supplier links and par stocks without creating it again.") : ""}
          closeLabel={t("Dismiss notice")} onDismiss={dismissErrorNotice} />}
      </div>

      {currentPicker && (
        <div
          className="aip-pickerWrap"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closePicker();
          }}
        >
          <div className="aip-picker" ref={pickerRef} role="dialog" aria-modal={!isSplit} aria-labelledby={`${dialogId}-picker-title`} aria-hidden={supplierCreator ? true : undefined} inert={supplierCreator ? "" : undefined}>
            <div className="aip-pickerHead">
              <div>
                <h6 id={`${dialogId}-picker-title`}>{t(currentPicker.title)}</h6>
                <p>{t(currentPicker.hint)}</p>
              </div>
              <button
                type="button"
                className="aip-closeBtn"
                aria-label={t("Close selector")}
                onClick={closePicker}
              >
                ×
              </button>
            </div>

            <div className="aip-pickerBody">
              {canCreateSupplier && ["add_supplier", "assigned_supplier"].includes(fieldPicker.key) && (
                <button type="button" className="aip-btn aip-btn--primary" onClick={openSupplierCreator}>
                  {t("Create supplier")}
                </button>
              )}
              <input
                className="form-control"
                autoFocus
                value={fieldPicker.query}
                onChange={(event) =>
                  setFieldPicker((prev) => ({ ...prev, query: event.target.value }))
                }
                aria-label={t("Search options…")}
                placeholder={t("Search options…")}
              />

              <div className="aip-pickerList">
                {filteredPickerOptions.length ? (
                  filteredPickerOptions.map((option) => (
                    <button
                      key={`${fieldPicker.key}-${option.value || "empty"}-${option.label}`}
                      type="button"
                      className="aip-pickerItem"
                      aria-pressed={String(option.value) === String(fieldPicker.key === "assigned_supplier" ? selectedSupplierIds[0] || "" : form[fieldPicker.key])}
                      onClick={() => applyPickerSelection(option.value)}
                    >
                      <span className="aip-pickerItemTitle">{option.label}</span>
                      {option.meta ? <span className="aip-pickerItemMeta">{option.meta}</span> : null}
                    </button>
                  ))
                ) : (
                  <div className="aip-pickerEmpty">{t(fieldPicker.query.trim() ? "No matching options." : currentPicker.emptyText)}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {supplierCreator && <AddSupplierPopup isOpen requiredUsage="stock" businessUnitId={supplierCreator.businessUnitId}
        onAdded={supplierAdded} onPending={request => supplierAdded(pendingReferenceRow(request, languageId))} onClose={closeSupplierCreator} />}
    </div>
  );
}
